// src/products/capRaise/pages/NewRaisePage.js
// New Raise page for creating and managing fundraising rounds

import React, { useState, useEffect, useCallback, useMemo } from 'react';
// import { useNavigate } from 'react-router-dom'; // Unused
import { 
  Save, 
  Target,
  Users,
  Settings,
  Shield,
  ChevronRight,
  Building,
  Plus
} from 'lucide-react';
import capRaiseApi from '../services/capRaiseApi';
import CurrencyService from '../../../services/currencyService';
import CountriesService from '../../../services/countriesService';
import MagnitudeSelector, { useMagnitude } from '../../../components/ui/MagnitudeSelector';
// import RoundProgressBar from '../components/RoundProgressBar'; // Unused

const COMMON_BLACKLIST = ['China', 'Russia', 'Iran', 'North Korea', 'Syria', 'Cuba'];

const NewRaisePage = ({ selectedCompany, activeRound, user, addMessage, onRoundChange }) => {
  // const navigate = useNavigate(); // Unused
  const [activeTab, setActiveTab] = useState('key_inputs');
  const [saving, setSaving] = useState(false);
  const [roundStatus, setRoundStatus] = useState('draft');
  // eslint-disable-next-line no-unused-vars
  const [workspaceInsights, setWorkspaceInsights] = useState({
    totalRaised: 0,
    investorCount: 0,
    committedCount: 0,
    lastSnapshot: null
  });
  // eslint-disable-next-line no-unused-vars
  const [insightsLoading, setInsightsLoading] = useState(false);

  // Key Inputs Tab
  const [roundName, setRoundName] = useState('');
  const [stage, setStage] = useState('seed');
  const [fundingInstrument, setFundingInstrument] = useState('preference');
  const [currency, setCurrency] = useState('USD');
  const [targetAmount, setTargetAmount] = useState('');
  const [preMoneyValuation, setPreMoneyValuation] = useState('');
  const [equityPercentage, setEquityPercentage] = useState('');
  
  // Magnitude selector state
  const [magnitude, setMagnitude] = useState('millions');
  const { toActualValue, toDisplayValue, getPlaceholder, getStepValue, formatCurrencyWithMagnitude } = useMagnitude(magnitude);
  
  // Memoize the conversion functions to prevent re-renders
  const convertToActual = useCallback((value) => {
    return toActualValue(value);
  }, [toActualValue]);
  
  const convertToDisplay = useCallback((value) => {
    return toDisplayValue(value);
  }, [toDisplayValue]);

  useEffect(() => {
    const loadInsights = async () => {
      if (!activeRound?.id) {
        setWorkspaceInsights({
          totalRaised: 0,
          investorCount: 0,
          committedCount: 0,
          lastSnapshot: null
        });
        return;
      }

      try {
        setInsightsLoading(true);
        const { data, error } = await capRaiseApi.getInvestors(activeRound.id);
        if (error) throw error;
        const investors = data || [];
        const committedInvestors = investors.filter(inv => ['committed', 'allocated'].includes(inv.status));
        const totalRaised = committedInvestors.reduce(
          (sum, inv) => sum + (inv.investment_amount || inv.commitment_amount || 0),
          0
        );

        setWorkspaceInsights({
          totalRaised,
          investorCount: investors.length,
          committedCount: committedInvestors.length,
          lastSnapshot: new Date().toISOString()
        });
      } catch (error) {
        console.error('Workspace insights error:', error);
      } finally {
        setInsightsLoading(false);
      }
    };

    loadInsights();
  }, [activeRound]);

  // Countries list (loaded from database)
  const [allCountries, setAllCountries] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loadingCountries, setLoadingCountries] = useState(true);

  // Round Details Tab
  const [roundDescription, setRoundDescription] = useState('');
  const [offerJurisdictionCountry, setOfferJurisdictionCountry] = useState('');
  const [investorResidencyRequirement, setInvestorResidencyRequirement] = useState('any_except_excluded');
  const [specificResidencyCountries, setSpecificResidencyCountries] = useState([]);
  const [excludedCountries, setExcludedCountries] = useState([]);
  const [showCountrySelector, setShowCountrySelector] = useState(false);
  const [showResidencySelector, setShowResidencySelector] = useState(false);
  const [minInvestment, setMinInvestment] = useState('');
  const [maxInvestment, setMaxInvestment] = useState('');
  const [leadInvestorAmount, setLeadInvestorAmount] = useState('');

  // Governance Tab
  const [boardSeats, setBoardSeats] = useState('');
  const [observerRights, setObserverRights] = useState(false);
  const [protectiveProvisions, setProtectiveProvisions] = useState([]);
  const [dragAlongRights, setDragAlongRights] = useState(false);
  const [tagAlongRights, setTagAlongRights] = useState(false);
  const [antiDilutionProvisions, setAntiDilutionProvisions] = useState('none');
  const [liquidationPreference, setLiquidationPreference] = useState('1x non-participating');
  const [participationRights, setParticipationRights] = useState(false);
  const [informationRights, setInformationRights] = useState(false);
  const [proRataRights, setProRataRights] = useState(false);
  const [rightOfFirstRefusal, setRightOfFirstRefusal] = useState(false);
  const [coSaleRights, setCoSaleRights] = useState(false);
  
  // Employee Share Plan
  const [hasEmployeeSharePlan, setHasEmployeeSharePlan] = useState(false);
  const [employeeSharePoolSize, setEmployeeSharePoolSize] = useState('10');
  const [employeeSharePoolType, setEmployeeSharePoolType] = useState('percentage');
  const [employeeSharePoolTiming, setEmployeeSharePoolTiming] = useState('pre-money');
  
  // Use of funds
  const [useOfFunds, setUseOfFunds] = useState([
    { category: 'Product Development', percentage: 40, amount: '' },
    { category: 'Sales & Marketing', percentage: 30, amount: '' },
    { category: 'Operations', percentage: 20, amount: '' },
    { category: 'Working Capital', percentage: 10, amount: '' }
  ]);


  // Tabs configuration
  const tabs = [
    { id: 'key_inputs', label: 'Key Inputs', icon: Target, description: 'Round basics & targets' },
    { id: 'round_details', label: 'Investment', icon: Settings, description: 'Jurisdiction & limits' },
    { id: 'governance', label: 'Governance', icon: Shield, description: 'Rights & board controls' },
    { id: 'use_of_funds', label: 'Use of Funds', icon: Users, description: 'Capital deployment' }
  ];

  const summaryTargetAmount = useMemo(() => {
    if (activeRound?.target_amount) return activeRound.target_amount;
    if (targetAmount) {
      return convertToActual(targetAmount);
    }
    return 0;
  }, [activeRound?.target_amount, targetAmount, convertToActual]);

  // const progressPercent = useMemo(() => {
  //   if (!summaryTargetAmount) return 0;
  //   return Math.min((workspaceInsights.totalRaised / summaryTargetAmount) * 100, 100);
  // }, [summaryTargetAmount, workspaceInsights.totalRaised]); // Unused

  const companyDisplayName = selectedCompany?.company_name || selectedCompany?.name || 'Select a company';
  // const statusLabel = roundStatus.replace(/_/g, ' '); // Unused
  const statusStyles =
    roundStatus === 'active'
      ? 'bg-green-100 text-green-800'
      : roundStatus === 'closed'
      ? 'bg-gray-100 text-gray-700'
      : 'bg-yellow-100 text-yellow-800';
  // eslint-disable-next-line no-unused-vars
  const summaryCurrency = activeRound?.currency || currency;
  const formattedTarget = summaryTargetAmount
    ? formatCurrencyWithMagnitude(summaryTargetAmount)
    : 'Set a target';
  // const formattedRaised = workspaceInsights.totalRaised
  //   ? capRaiseApi.formatCurrency(workspaceInsights.totalRaised, summaryCurrency)
  //   : (insightsLoading ? 'Loading...' : '0'); // Unused

  // Load existing round data if editing, or reset form if creating new
  useEffect(() => {
    if (activeRound) {
      setRoundName(activeRound.round_name || '');
      setStage(activeRound.round_type || 'seed');
      setRoundStatus(activeRound.status || 'draft');
      // Convert actual values to display values based on magnitude
      const targetDisplay = convertToDisplay(activeRound.target_amount || 0);
      const preMoneyDisplay = convertToDisplay(activeRound.pre_money_valuation || 0);
      setTargetAmount(targetDisplay.toString());
      setPreMoneyValuation(preMoneyDisplay.toString());
      setMinInvestment(activeRound.minimum_amount?.toString() || '');
      setMaxInvestment(activeRound.maximum_amount?.toString() || '');
      setRoundDescription(activeRound.description || '');
      
      // Load terms config if available
      const termsConfig = activeRound.terms_config || {};
      const governance = termsConfig.governance || {};
      const employeePlan = termsConfig.employee_plan || {};
      const restrictions = termsConfig.restrictions || {};
      
      // Load jurisdiction settings
      setOfferJurisdictionCountry(restrictions.offer_jurisdiction_country || '');
      setInvestorResidencyRequirement(restrictions.investor_residency_requirement || 'any_except_excluded');
      setSpecificResidencyCountries(restrictions.specific_residency_countries || []);
      setExcludedCountries(restrictions.excluded_countries || []);
      
      setDragAlongRights(governance.includeDragAlong ?? false);
      setTagAlongRights(governance.includeTagAlong ?? false);
      setAntiDilutionProvisions(governance.includeAntiDilution ? 'weighted_average' : 'none');
      setParticipationRights(governance.includeParticipationRights ?? false);
      setInformationRights(governance.includeInformationRights ?? false);
      setProRataRights(governance.includeProRataRights ?? false);
      setRightOfFirstRefusal(governance.includeRightOfFirstRefusal ?? false);
      setCoSaleRights(governance.includeCoSaleRights ?? false);
      
      setHasEmployeeSharePlan(employeePlan.hasEmployeeSharePlan ?? false);
      setEmployeeSharePoolSize(employeePlan.employeeSharePoolSize?.toString() || '10');
      setEmployeeSharePoolType(employeePlan.employeeSharePoolType || 'percentage');
      setEmployeeSharePoolTiming(employeePlan.employeeSharePoolTiming || 'pre-money');
    } else {
      // Reset form to initial state when creating a new round
      setRoundName('');
      setStage('seed');
      setRoundStatus('draft');
      setTargetAmount('');
      setPreMoneyValuation('');
      setMinInvestment('');
      setMaxInvestment('');
      setRoundDescription('');
      setOfferJurisdictionCountry('');
      setInvestorResidencyRequirement('any_except_excluded');
      setSpecificResidencyCountries([]);
      setExcludedCountries([]);
      setDragAlongRights(false);
      setTagAlongRights(false);
      setAntiDilutionProvisions('none');
      setParticipationRights(false);
      setInformationRights(false);
      setProRataRights(false);
      setRightOfFirstRefusal(false);
      setCoSaleRights(false);
      setHasEmployeeSharePlan(false);
      setEmployeeSharePoolSize('10');
      setEmployeeSharePoolType('percentage');
      setEmployeeSharePoolTiming('pre-money');
    }
  }, [activeRound, convertToDisplay]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate equity percentage when target amount or pre-money changes
  useEffect(() => {
    if (targetAmount && preMoneyValuation) {
      const target = convertToActual(targetAmount);
      const preMoney = convertToActual(preMoneyValuation);
      if (target && preMoney) {
        const equity = (target / (preMoney + target)) * 100;
        setEquityPercentage(equity.toFixed(2));
      }
    }
  }, [targetAmount, preMoneyValuation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update use of funds amounts when target amount changes
  useEffect(() => {
    if (targetAmount) {
      const target = convertToActual(targetAmount);
      setUseOfFunds(prev => prev.map(item => ({
        ...item,
        amount: (target * item.percentage / 100).toLocaleString()
      })));
    }
  }, [targetAmount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load countries from database (all countries for jurisdiction selection)
  useEffect(() => {
    const loadCountries = async () => {
      try {
        setLoadingCountries(true);
        const countryNames = await CountriesService.getAllCountryNames();
        setAllCountries(countryNames);
      } catch (error) {
        console.error('Error loading countries:', error);
        // Fallback to empty array - will show no countries
        setAllCountries([]);
      } finally {
        setLoadingCountries(false);
      }
    };
    loadCountries();
  }, []);

  // Handle protective provisions change
  const toggleProtectiveProvision = (provision) => {
    setProtectiveProvisions(prev => 
      prev.includes(provision)
        ? prev.filter(p => p !== provision)
        : [...prev, provision]
    );
  };

  // Handle use of funds percentage change
  const updateUseOfFunds = (index, field, value) => {
    setUseOfFunds(prev => {
      const newFunds = [...prev];
      newFunds[index] = { ...newFunds[index], [field]: value };
      
      // Recalculate amounts if percentage changed
      if (field === 'percentage' && targetAmount) {
        const target = convertToActual(targetAmount);
        newFunds[index].amount = (target * value / 100).toLocaleString();
      }
      
      return newFunds;
    });
  };


  // Handle excluded country toggle
  const toggleExcludedCountry = (country) => {
    setExcludedCountries(prev => 
      prev.includes(country)
        ? prev.filter(c => c !== country)
        : [...prev, country]
    );
  };

  // Handle specific residency country toggle
  const toggleSpecificResidencyCountry = (country) => {
    setSpecificResidencyCountries(prev => 
      prev.includes(country)
        ? prev.filter(c => c !== country)
        : [...prev, country]
    );
  };

  // Get residency requirement label
  const getResidencyRequirementLabel = () => {
    switch (investorResidencyRequirement) {
      case 'any_except_excluded':
        return 'Any Country except Excluded';
      case 'specific_countries':
        return 'Specific Countries';
      case 'offer_jurisdiction':
        return offerJurisdictionCountry ? `${offerJurisdictionCountry} (Country of Offer Jurisdiction)` : 'Country of Offer Jurisdiction';
      default:
        return 'Any Country except Excluded';
    }
  };

  // Handle status change
  const handleStatusChange = async (newStatus) => {
    if (!activeRound?.id) {
      // If no round exists yet, just update local state
      setRoundStatus(newStatus);
      return;
    }

    try {
      const result = await capRaiseApi.updateRound(activeRound.id, { status: newStatus });
      if (result.error) {
        addMessage?.(`Error updating status: ${result.error.message}`, 'error');
      } else {
        setRoundStatus(newStatus);
        onRoundChange?.(result.data);
        addMessage?.('Status updated successfully', 'success');
      }
    } catch (error) {
      console.error('Status update error:', error);
      addMessage?.('Error updating status: ' + error.message, 'error');
    }
  };

  // Handle creating a new round (clears form)
  const handleCreateNewRound = () => {
    onRoundChange?.(null);
    // Reset form by clearing activeRound
    setRoundName('');
    setStage('seed');
    setRoundStatus('draft');
    setTargetAmount('');
    setPreMoneyValuation('');
    setMinInvestment('');
    setMaxInvestment('');
    setRoundDescription('');
    setOfferJurisdictionCountry('');
    setInvestorResidencyRequirement('any_except_excluded');
    setSpecificResidencyCountries([]);
    setExcludedCountries([]);
    setDragAlongRights(false);
    setTagAlongRights(false);
    setAntiDilutionProvisions('none');
    setParticipationRights(false);
    setInformationRights(false);
    setProRataRights(false);
    setRightOfFirstRefusal(false);
    setCoSaleRights(false);
    setHasEmployeeSharePlan(false);
    setEmployeeSharePoolSize('10');
    setEmployeeSharePoolType('percentage');
    setEmployeeSharePoolTiming('pre-money');
  };

  // Save round data
  const handleSave = async () => {
    if (!selectedCompany) {
      addMessage?.('Please select a company first', 'error');
      return;
    }

    if (!roundName.trim()) {
      addMessage?.('Please enter a round name', 'error');
      return;
    }

    if (!targetAmount || convertToActual(targetAmount) <= 0) {
      addMessage?.('Please enter a valid target amount', 'error');
      return;
    }

    setSaving(true);

    try {
      const roundData = {
        company_id: selectedCompany.id,
        round_name: roundName.trim(),
        round_type: stage,
        target_amount: convertToActual(targetAmount),
        minimum_amount: minInvestment ? parseFloat(minInvestment) : null,
        maximum_amount: maxInvestment ? parseFloat(maxInvestment) : null,
        pre_money_valuation: preMoneyValuation ? convertToActual(preMoneyValuation) : null,
        post_money_valuation: convertToActual(preMoneyValuation) + convertToActual(targetAmount),
        share_price: null,
        description: roundDescription.trim() || null,
        status: roundStatus,
        dataroom_enabled: true,
        auto_allocation: false,
        oversubscription_allowed: true,
        maximum_oversubscription_percent: 20.00,
        notes: `Created via Round Builder. Funding instrument: ${fundingInstrument}, Currency: ${currency}`,
        terms_config: {
          governance: {
            includeDragAlong: dragAlongRights,
            includeTagAlong: tagAlongRights,
            includeAntiDilution: antiDilutionProvisions !== 'none',
            includeLiquidationPreference: liquidationPreference !== 'none',
            includeParticipationRights: participationRights,
            includeInformationRights: informationRights,
            includeProRataRights: proRataRights,
            includeRightOfFirstRefusal: rightOfFirstRefusal,
            includeCoSaleRights: coSaleRights,
            includeBoardMeetingRights: observerRights,
            includeConsentRights: protectiveProvisions.length > 0,
            boardSeats: boardSeats,
            antiDilutionType: antiDilutionProvisions,
            liquidationPreference: liquidationPreference,
            protectiveProvisions: protectiveProvisions
          },
          employee_plan: {
            hasEmployeeSharePlan,
            employeeSharePoolSize: hasEmployeeSharePlan ? parseFloat(employeeSharePoolSize) || 0 : 0,
            employeeSharePoolType,
            employeeSharePoolTiming
          },
          use_of_funds: useOfFunds,
          funding_details: {
            stage,
            fundingInstrument,
            currency,
            equityPercentage: parseFloat(equityPercentage) || 0,
            leadInvestorAmount: leadInvestorAmount ? parseFloat(leadInvestorAmount) : null
          },
          restrictions: {
            offer_jurisdiction_country: offerJurisdictionCountry,
            investor_residency_requirement: investorResidencyRequirement,
            specific_residency_countries: specificResidencyCountries,
            excluded_countries: excludedCountries
          }
        }
      };


      let result;
      if (activeRound) {
        result = await capRaiseApi.updateRound(activeRound.id, roundData);
      } else {
        result = await capRaiseApi.createRound(roundData);
      }

      if (result.error) {
        console.error('API Error:', result.error);
        addMessage?.(`Error saving round: ${result.error.message}`, 'error');
      } else {
        addMessage?.('Round saved successfully!', 'success');
        onRoundChange?.(result.data);
      }
    } catch (error) {
      console.error('Save error:', error);
      addMessage?.('Error saving round: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Render Use of Funds tab content
  const renderUseOfFundsTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Allocation of Funds</h3>
        <div className="space-y-4">
          {useOfFunds.map((item, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border border-gray-200 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <input
                  type="text"
                  value={item.category}
                  onChange={(e) => updateUseOfFunds(index, 'category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Percentage (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={item.percentage}
                  onChange={(e) => updateUseOfFunds(index, 'percentage', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount ({currency})
                </label>
                <input
                  type="text"
                  value={item.amount}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            Total allocation: {useOfFunds.reduce((sum, item) => sum + item.percentage, 0)}%
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-2xl border shadow-sm p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Round workspace</p>
            <h1 className="text-3xl font-bold text-gray-900">
              {roundName || activeRound?.round_name || 'Create a new round'}
              {formattedTarget !== 'Set a target' && ` for ${formattedTarget}`}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-gray-500">Status:</label>
                <select
                  value={roundStatus}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer transition-colors ${statusStyles}`}
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <span className="inline-flex items-center text-gray-500">
                <Building className="w-4 h-4 mr-2" />
                {companyDisplayName}
              </span>
              {activeRound?.updated_at && (
                <span className="text-gray-400">
                  Updated {(() => {
                    try {
                      const date = new Date(activeRound.updated_at);
                      if (isNaN(date.getTime())) return 'N/A';
                      const day = date.getDate();
                      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                      const month = monthNames[date.getMonth()];
                      const year = date.getFullYear();
                      return `${day} ${month} ${year}`;
                    } catch {
                      return 'N/A';
                    }
                  })()}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleCreateNewRound}
              className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-md shadow-sm text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create New Round
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !selectedCompany}
              className="inline-flex items-center px-5 py-2 text-sm font-semibold rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Round'}
            </button>
          </div>
        </div>
      </section>

      {!selectedCompany && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-800">
            ⚠️ Please select a company from the header dropdown before creating a round.
          </p>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        <aside className="lg:w-72">
          <div className="bg-white border rounded-2xl p-4 space-y-4 lg:sticky lg:top-24">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Workspace Sections
              </p>
              <div className="space-y-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center justify-between rounded-2xl px-3 py-3 border transition-colors ${
                        isActive
                          ? 'border-blue-200 bg-blue-50 text-blue-700'
                          : 'border-transparent bg-gray-50 text-gray-700 hover:border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-3 text-left">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isActive ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-500 border border-gray-200'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold">{tab.label}</p>
                          <p className="text-xs text-gray-500">{tab.description}</p>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-2xl bg-blue-50 p-4 text-sm text-blue-900">
              <p className="font-semibold mb-1">Pro tip</p>
              <p>Use the section menu to jump quickly. Changes persist once you hit “Save changes”.</p>
            </div>

            <button
              onClick={handleSave}
              disabled={saving || !selectedCompany}
              className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-semibold rounded-xl text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save workspace'}
            </button>
          </div>
        </aside>

        <section className="flex-1">
          <div className="bg-white rounded-2xl border p-6 space-y-6">
        {/* Key Inputs Tab */}
        {activeTab === 'key_inputs' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Round Name *
                </label>
                <input
                  type="text"
                  value={roundName}
                  onChange={(e) => setRoundName(e.target.value)}
                  placeholder="e.g., Seed Round 2024"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Funding Stage
                </label>
                <select
                  value={stage}
                  onChange={(e) => setStage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="pre-seed">Pre-Seed</option>
                  <option value="seed">Seed</option>
                  <option value="series-a">Series A</option>
                  <option value="series-b">Series B</option>
                  <option value="series-c">Series C</option>
                  <option value="bridge">Bridge</option>
                  <option value="convertible">Convertible</option>
                  <option value="growth">Growth</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Funding Instrument
                </label>
                <select
                  value={fundingInstrument}
                  onChange={(e) => setFundingInstrument(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="preference">Preference Shares</option>
                  <option value="ordinary">Ordinary Shares</option>
                  <option value="convertible">Convertible Note</option>
                  <option value="safe">SAFE</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Currency
                </label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {CurrencyService.getActiveCurrencies().map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.flag} {currency.code} - {currency.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Magnitude Selector */}
              <div className="md:col-span-2">
                <MagnitudeSelector 
                  value={magnitude}
                  onChange={setMagnitude}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Amount * ({currency})
                </label>
                <input
                  type="number"
                  value={targetAmount}
                  onChange={(e) => setTargetAmount(e.target.value)}
                  placeholder={getPlaceholder(1000000)}
                  step={getStepValue()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {targetAmount && (
                  <p className="text-xs text-gray-500 mt-1">
                    Actual: {formatCurrencyWithMagnitude(toActualValue(targetAmount))}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pre-Money Valuation ({currency})
                </label>
                <input
                  type="number"
                  value={preMoneyValuation}
                  onChange={(e) => setPreMoneyValuation(e.target.value)}
                  placeholder={getPlaceholder(5000000)}
                  step={getStepValue()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {preMoneyValuation && (
                  <p className="text-xs text-gray-500 mt-1">
                    Actual: {formatCurrencyWithMagnitude(toActualValue(preMoneyValuation))}
                  </p>
                )}
              </div>

              {equityPercentage && (
                <div className="md:col-span-2">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-blue-900">
                      Equity Percentage: {equityPercentage}%
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      Post-Money Valuation: {currency} {(convertToActual(preMoneyValuation) + convertToActual(targetAmount)).toLocaleString()}
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Round Description
                    </label>
                    <textarea
                      value={roundDescription}
                      onChange={(e) => setRoundDescription(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Short elevator pitch for this raise..."
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Round Details Tab */}
            {activeTab === 'round_details' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Investment ({currency})
                </label>
                <input
                  type="number"
                  value={minInvestment}
                  onChange={(e) => setMinInvestment(e.target.value)}
                  placeholder="10000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Investment ({currency})
                </label>
                <input
                  type="number"
                  value={maxInvestment}
                  onChange={(e) => setMaxInvestment(e.target.value)}
                  placeholder="100000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lead Investor Amount ({currency})
                </label>
                <input
                  type="number"
                  value={leadInvestorAmount}
                  onChange={(e) => setLeadInvestorAmount(e.target.value)}
                  placeholder="250000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Jurisdiction Settings */}
            <div className="space-y-6 border-t pt-6">
              <h4 className="text-md font-medium text-gray-900">Jurisdiction & Residency Requirements</h4>
              
              {/* Shares Issued Under Country Rules */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shares Issued under <span className="font-bold">[Country Name]</span> Rules
                </label>
                <select
                  value={offerJurisdictionCountry}
                  onChange={(e) => setOfferJurisdictionCountry(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Country</option>
                  {allCountries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
                {offerJurisdictionCountry && (
                  <p className="mt-2 text-sm text-gray-600">
                    Shares will be issued under <strong>{offerJurisdictionCountry}</strong> rules
                  </p>
                )}
              </div>

              {/* Investor Residency Requirement */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Investors or Investor Entity must be resident of:
                </label>
                <select
                  value={investorResidencyRequirement}
                  onChange={(e) => {
                    setInvestorResidencyRequirement(e.target.value);
                    // Clear specific countries if switching away
                    if (e.target.value !== 'specific_countries') {
                      setSpecificResidencyCountries([]);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="any_except_excluded">Any Country except Excluded</option>
                  <option value="specific_countries">Specific Countries</option>
                  <option 
                    value="offer_jurisdiction"
                    disabled={!offerJurisdictionCountry}
                  >
                    {offerJurisdictionCountry ? `${offerJurisdictionCountry} (Country of Offer Jurisdiction)` : '[Select Offer Jurisdiction First]'}
                  </option>
                </select>
                {investorResidencyRequirement === 'offer_jurisdiction' && !offerJurisdictionCountry && (
                  <p className="mt-2 text-sm text-amber-600">
                    Please select the offer jurisdiction country above first
                  </p>
                )}
              </div>

              {/* Specific Residency Countries Selector */}
              {investorResidencyRequirement === 'specific_countries' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Specific Residency Countries
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowResidencySelector(!showResidencySelector)}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      {showResidencySelector ? 'Hide' : 'Add Countries'}
                    </button>
                  </div>
                  {specificResidencyCountries.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {specificResidencyCountries.map(country => (
                        <span
                          key={country}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {country}
                          <button
                            onClick={() => toggleSpecificResidencyCountry(country)}
                            className="ml-1 h-3 w-3 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {showResidencySelector && (
                    <div className="border border-gray-200 rounded-md p-4 bg-gray-50 max-h-48 overflow-y-auto">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {allCountries
                          .filter(country => !specificResidencyCountries.includes(country))
                          .map(country => (
                            <button
                              key={country}
                              type="button"
                              onClick={() => toggleSpecificResidencyCountry(country)}
                              className="text-left p-2 text-sm hover:bg-white rounded border border-transparent hover:border-gray-300"
                            >
                              {country}
                            </button>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Excluded Countries */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Excluded Countries
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowCountrySelector(!showCountrySelector)}
                    className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    {showCountrySelector ? 'Hide' : 'Add Countries'}
                  </button>
                </div>
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1">Common blacklist</p>
                  <div className="flex flex-wrap gap-2">
                    {COMMON_BLACKLIST.map((country) => {
                      const isAdded = excludedCountries.includes(country);
                      return (
                        <button
                          key={country}
                          type="button"
                          onClick={() => toggleExcludedCountry(country)}
                          disabled={isAdded}
                          className={`px-2 py-1 text-xs rounded-full border ${
                            isAdded
                              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              : 'bg-white text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {isAdded ? `✓ ${country}` : `Add ${country}`}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {excludedCountries.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {excludedCountries.map(country => (
                      <span
                        key={country}
                        className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-100 text-red-800"
                      >
                        {country}
                        <button
                          onClick={() => toggleExcludedCountry(country)}
                          className="ml-1 h-3 w-3 text-red-600 hover:text-red-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {showCountrySelector && (
                  <div className="border border-gray-200 rounded-md p-4 bg-gray-50 max-h-48 overflow-y-auto">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {allCountries
                        .filter(country => !excludedCountries.includes(country))
                        .map(country => (
                          <button
                            key={country}
                            type="button"
                            onClick={() => toggleExcludedCountry(country)}
                            className="text-left p-2 text-sm hover:bg-white rounded border border-transparent hover:border-gray-300"
                          >
                            {country}
                          </button>
                        ))
                      }
                    </div>
                  </div>
                )}
                {investorResidencyRequirement === 'any_except_excluded' && excludedCountries.length === 0 && (
                  <p className="mt-2 text-sm text-gray-500">
                    No countries are excluded. All investors from any country are eligible.
                  </p>
                )}
              </div>

              {/* Summary */}
              {(offerJurisdictionCountry || investorResidencyRequirement !== 'any_except_excluded' || excludedCountries.length > 0) && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <h5 className="text-sm font-medium text-blue-900 mb-2">Summary</h5>
                  <ul className="text-sm text-blue-800 space-y-1">
                    {offerJurisdictionCountry && (
                      <li>• Shares issued under <strong>{offerJurisdictionCountry}</strong> rules</li>
                    )}
                    <li>• Investor residency: <strong>{getResidencyRequirementLabel()}</strong></li>
                    {investorResidencyRequirement === 'specific_countries' && specificResidencyCountries.length > 0 && (
                      <li>• Specific countries: {specificResidencyCountries.join(', ')}</li>
                    )}
                    {excludedCountries.length > 0 && (
                      <li>• Excluded countries: {excludedCountries.join(', ')}</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Governance Tab */}
        {activeTab === 'governance' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Board and Voting</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Board Seats for Investors
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={boardSeats}
                    onChange={(e) => setBoardSeats(e.target.value)}
                    placeholder="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="observerRights"
                    checked={observerRights}
                    onChange={(e) => setObserverRights(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="observerRights" className="ml-2 block text-sm text-gray-700">
                    Board Observer Rights
                  </label>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Protective Provisions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'Amendment of charter or bylaws',
                  'Authorization of new shares',
                  'Declaration of dividends',
                  'Redemption of shares',
                  'Liquidation or winding up',
                  'Merger or acquisition',
                  'Sale of assets',
                  'Incurrence of debt'
                ].map((provision) => (
                  <div key={provision} className="flex items-center">
                    <input
                      type="checkbox"
                      id={provision}
                      checked={protectiveProvisions.includes(provision)}
                      onChange={() => toggleProtectiveProvision(provision)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor={provision} className="ml-2 block text-sm text-gray-700">
                      {provision}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Investor Rights</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="dragAlongRights"
                    checked={dragAlongRights}
                    onChange={(e) => setDragAlongRights(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="dragAlongRights" className="ml-2 block text-sm text-gray-700">
                    Drag-Along Rights
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="tagAlongRights"
                    checked={tagAlongRights}
                    onChange={(e) => setTagAlongRights(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="tagAlongRights" className="ml-2 block text-sm text-gray-700">
                    Tag-Along Rights
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="participationRights"
                    checked={participationRights}
                    onChange={(e) => setParticipationRights(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="participationRights" className="ml-2 block text-sm text-gray-700">
                    Participation Rights
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="informationRights"
                    checked={informationRights}
                    onChange={(e) => setInformationRights(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="informationRights" className="ml-2 block text-sm text-gray-700">
                    Information Rights
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="proRataRights"
                    checked={proRataRights}
                    onChange={(e) => setProRataRights(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="proRataRights" className="ml-2 block text-sm text-gray-700">
                    Pro-Rata Rights
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="rightOfFirstRefusal"
                    checked={rightOfFirstRefusal}
                    onChange={(e) => setRightOfFirstRefusal(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="rightOfFirstRefusal" className="ml-2 block text-sm text-gray-700">
                    Right of First Refusal
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="coSaleRights"
                    checked={coSaleRights}
                    onChange={(e) => setCoSaleRights(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="coSaleRights" className="ml-2 block text-sm text-gray-700">
                    Co-Sale Rights
                  </label>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Economic Terms</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Anti-Dilution Provisions
                  </label>
                  <select
                    value={antiDilutionProvisions}
                    onChange={(e) => setAntiDilutionProvisions(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="none">None</option>
                    <option value="full_ratchet">Full Ratchet</option>
                    <option value="weighted_average">Weighted Average</option>
                    <option value="narrow_based">Narrow-Based Weighted Average</option>
                    <option value="broad_based">Broad-Based Weighted Average</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Liquidation Preference
                  </label>
                  <select
                    value={liquidationPreference}
                    onChange={(e) => setLiquidationPreference(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="none">None</option>
                    <option value="1x non-participating">1x Non-Participating</option>
                    <option value="1x participating">1x Participating</option>
                    <option value="2x non-participating">2x Non-Participating</option>
                    <option value="2x participating">2x Participating</option>
                  </select>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Employee Share Plan</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasEmployeeSharePlan"
                    checked={hasEmployeeSharePlan}
                    onChange={(e) => setHasEmployeeSharePlan(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="hasEmployeeSharePlan" className="ml-2 block text-sm font-medium text-gray-700">
                    Include Employee Share Plan (ESOP/Stock Options)
                  </label>
                </div>

                {hasEmployeeSharePlan && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pl-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pool Size
                      </label>
                      <input
                        type="number"
                        value={employeeSharePoolSize}
                        onChange={(e) => setEmployeeSharePoolSize(e.target.value)}
                        placeholder="10"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pool Type
                      </label>
                      <select
                        value={employeeSharePoolType}
                        onChange={(e) => setEmployeeSharePoolType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="percentage">Percentage</option>
                        <option value="shares">Number of Shares</option>
                        <option value="dollar">Dollar Value</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pool Timing
                      </label>
                      <select
                        value={employeeSharePoolTiming}
                        onChange={(e) => setEmployeeSharePoolTiming(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="pre-money">Pre-Money</option>
                        <option value="post-money">Post-Money</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Use of Funds Tab */}
        {activeTab === 'use_of_funds' && renderUseOfFundsTab()}

          </div>
        </section>
      </div>
    </div>
  );
};

export default NewRaisePage;

